import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR } from "../lib/api";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { AgingBar } from "../components/AgingBar";
import { IndianRupee, Truck, Wallet, AlertTriangle, ArrowUpRight, TrendingUp } from "lucide-react";

const StatusPill = ({ status }) => {
  const map = {
    delivered: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    partial: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
    pending: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
    paid: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    unpaid: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${map[status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
      {status}
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white border border-[#E6E4DD] rounded-sm p-5 hover:border-[#1C2331] transition-colors">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-[0.1em] text-[#8CA3A7]">{label}</span>
      <Icon className={`w-4 h-4 ${accent || "text-[#8CA3A7]"}`} />
    </div>
    <div className="mt-4 font-mono text-2xl tabular-nums text-[#0A0D14] tracking-tight">{value}</div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);

  if (!d) return <div className="text-slate-400 font-mono text-sm">Loading dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Overview</p>
        <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi testid="kpi-order-value" icon={IndianRupee} label="Total Order Value" value={fmtINR(d.total_order_value)} />
        <Kpi testid="kpi-pending-deliveries" icon={Truck} label="Pending Deliveries" value={d.pending_deliveries} />
        <Kpi testid="kpi-outstanding" icon={Wallet} label="Pending Payments" value={fmtINR(d.total_outstanding)} accent="text-[#D95D39]" />
        <Kpi testid="kpi-overdue" icon={AlertTriangle} label="Overdue Payments" value={fmtINR(d.overdue_amount)} accent="text-[#991B1B]" />
      </div>

      {d.top_overdue_buyers && d.top_overdue_buyers.length > 0 && (
        <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-sm p-6" data-testid="overdue-spotlight">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-[#991B1B] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Top Overdue Buyers
            </h2>
            <button onClick={() => navigate("/reminders")} className="text-sm text-[#991B1B] font-medium flex items-center gap-1 hover:gap-2 transition-all">
              View reminders <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {d.top_overdue_buyers.map((r) => (
              <div
                key={r.name}
                data-testid={`overdue-buyer-${r.name}`}
                onClick={() => navigate(`/parties/detail?name=${encodeURIComponent(r.name)}&role=buyer`)}
                className="flex items-center justify-between border-b border-[#FECACA]/60 pb-2 last:border-0 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <span className="text-sm text-[#7F1D1D] truncate pr-3">{r.name}</span>
                <span className="font-mono tabular-nums text-sm font-semibold text-[#991B1B]">{fmtINR(r.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {d.aging && <AgingBar aging={d.aging} />}

      {d.top_categories && d.top_categories.length > 0 && (
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-6" data-testid="category-exposure-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-[#0A0D14] flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#D95D39]" /> Category Seller Exposure
            </h2>
            <button onClick={() => navigate("/parties")} className="text-sm text-[#8CA3A7] font-medium flex items-center gap-1 hover:text-[#1C2331] transition-colors">
              Reports <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            {(() => {
              const max = Math.max(...d.top_categories.map((c) => c.value), 1);
              return d.top_categories.map((c) => (
                <div key={c.category} data-testid={`category-row-${c.category}`} onClick={() => navigate(`/reports/category?category=${encodeURIComponent(c.category)}`)} className="cursor-pointer group">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[#0A0D14] font-medium group-hover:text-[#D95D39] transition-colors">{c.category} <span className="text-xs text-[#8CA3A7] font-mono">· {c.orders} orders</span></span>
                    <span className="font-mono tabular-nums text-[#0A0D14]">{fmtINR(c.value)} <span className="text-[#D95D39]">· {fmtINR(c.outstanding)} due</span></span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div style={{ width: `${(c.value / max) * 100}%` }} className="h-full bg-[#1C2331]" />
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E6E4DD] rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-lg font-semibold text-[#0A0D14]">Monthly Order Value</h2>
              <p className="text-xs text-[#8CA3A7] mt-0.5">Last 6 months · tap a point to see that month's orders</p>
            </div>
            <TrendingUp className="w-4 h-4 text-[#D95D39]" />
          </div>
          {d.monthly_trends.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-sm text-slate-400 font-mono">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <LineChart
                data={d.monthly_trends}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                onClick={(state) => { if (state && state.activeLabel) navigate(`/orders?month=${state.activeLabel}`); }}
                style={{ cursor: "pointer" }}
              >
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#8CA3A7" }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => fmtINR(v)}
                  contentStyle={{ border: "1px solid #E6E4DD", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#D95D39"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#1C2331" }}
                  activeDot={{ r: 6, fill: "#D95D39", cursor: "pointer", onClick: (e, p) => { const m = p?.payload?.month; if (m) navigate(`/orders?month=${m}`); } }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E6E4DD] rounded-sm p-6">
          <h2 className="font-heading text-lg font-semibold text-[#0A0D14] mb-4">Order Status</h2>
          <div className="space-y-3">
            {[
              ["Delivered", d.status_counts.delivered, "#065F46"],
              ["Partial", d.status_counts.partial, "#92400E"],
              ["Pending", d.status_counts.pending, "#1E40AF"],
            ].map(([label, count, color]) => (
              <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                <span className="text-sm text-[#4B5563]">{label}</span>
                <span className="font-mono tabular-nums text-lg" style={{ color }}>{count}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm font-medium text-[#0A0D14]">Total orders</span>
              <span className="font-mono tabular-nums text-lg text-[#0A0D14]">{d.total_orders}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[
          ["Top Buyers", d.top_buyers, "top-buyers"],
          ["Top Sellers", d.top_sellers, "top-sellers"],
        ].map(([title, rows, tid]) => (
          <div key={title} className="bg-white border border-[#E6E4DD] rounded-sm p-6" data-testid={tid}>
            <h2 className="font-heading text-lg font-semibold text-[#0A0D14] mb-4">{title}</h2>
            {rows.length === 0 ? (
              <div className="text-sm text-slate-400 font-mono py-4">No data yet</div>
            ) : (
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.name} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                    <span className="text-sm text-[#4B5563] truncate pr-3">{r.name}</span>
                    <span className="font-mono tabular-nums text-sm text-[#0A0D14]">{fmtINR(r.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E6E4DD] rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-[#0A0D14]">Recent Orders</h2>
          <button data-testid="view-all-orders" onClick={() => navigate("/orders")} className="text-sm text-[#D95D39] font-medium flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
        {d.recent_orders.length === 0 ? (
          <div className="text-sm text-slate-400 font-mono py-6 text-center">No orders yet. Create your first order.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {d.recent_orders.map((o) => (
              <div
                key={o.id}
                data-testid={`recent-order-${o.id}`}
                onClick={() => navigate(`/orders/${o.id}`)}
                className="flex items-center justify-between py-3 cursor-pointer hover:bg-slate-50/60 -mx-2 px-2 rounded-sm transition-colors"
              >
                <div>
                  <div className="text-sm font-medium text-[#0A0D14]">{o.buyer_name || "Unknown buyer"} <span className="text-[#8CA3A7]">←</span> {o.seller_name || "Unknown seller"}</div>
                  <div className="text-xs text-[#8CA3A7] font-mono mt-0.5">{o.po_number || "No PO#"} · {o.fabric_type || "—"}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <StatusPill status={o.delivery_status} />
                    <StatusPill status={o.payment_status} />
                  </div>
                  <span className="font-mono tabular-nums text-sm text-[#0A0D14] w-24 text-right">{fmtINR(o.total_amount)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
